import React from 'react';
import { Box, Typography, Button } from '@mui/joy';
import { UsersThree as SupervisorAccount, ArrowsLeftRight as SwapHoriz, SignOut as Logout } from '@phosphor-icons/react';
import { useImpersonation } from '../../contexts/ImpersonationContext';

/**
 * Permanent, non-dismissible bar shown whenever a Super Admin is impersonating
 * another user. Fixed to the top of the viewport above the TopBar.
 */
export const IMPERSONATION_BAR_HEIGHT = 44;

export const ImpersonationBar: React.FC = () => {
  const { state, isImpersonating, openPicker, stopImpersonation } = useImpersonation();

  if (!isImpersonating || !state) return null;

  const { target, impersonator } = state;
  const orgName = target.organization?.name;
  const backLabel = impersonator?.name ? ` (back to ${impersonator.name})` : '';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${IMPERSONATION_BAR_HEIGHT}px`,
        zIndex: 1250, // above TopBar (999) and md sidebar (1000), below modals (1300)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, md: 3 },
        background: 'linear-gradient(90deg, #b71c1c 0%, #f57c00 100%)',
        color: 'white',
        boxShadow: 'sm',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <SupervisorAccount size={19} style={{ flexShrink: 0 }} />
        <Typography
          level="body-sm"
          sx={{ color: 'white', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          Impersonating {target.name}
          {orgName ? ` — ${orgName}` : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Button
          size="sm"
          variant="soft"
          color="neutral"
          startDecorator={<SwapHoriz />}
          onClick={openPicker}
          sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
        >
          Switch user
        </Button>
        <Button
          size="sm"
          variant="solid"
          color="neutral"
          startDecorator={<Logout />}
          onClick={stopImpersonation}
          sx={{ bgcolor: 'white', color: '#b71c1c', fontWeight: 'bold', '&:hover': { bgcolor: 'grey.100' } }}
        >
          Exit impersonation{backLabel}
        </Button>
      </Box>
    </Box>
  );
};
